import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '@lib/api/client';
import { LargeTitleHeader, PullToRefresh } from '@/components/common';

// ── Client quotes list (portal audit) — target of the "Review & Approve in
// Your Portal" button in quote emails.

interface QuoteRow { id: number; quote_number: string; status: string; total: number; valid_until: string | null; job_title: string | null; created_at: string }

const fmt = (n: unknown) => `$${(Number(n) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const chip: Record<string, { label: string; cls: string }> = {
  sent: { label: 'Awaiting your review', cls: 'bg-[#C77D3F]/15 text-[#8a5526]' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  declined: { label: 'Declined', cls: 'bg-gray-100 text-gray-500' },
};

export const ClientQuotesPage = () => {
  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () =>
    apiClient.get('/portal/me/quotes')
      .then(r => { setQuotes(r.data.data ?? []); setError(''); })
      .catch(() => setError('Could not load your quotes — pull down to retry.'))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  return (
    <PullToRefresh onRefresh={load}>
      <div className="space-y-4">
        <LargeTitleHeader title="Quotes" subtitle="Review and approve project quotes" />

        {loading && <div className="h-28 rounded-xl bg-gray-100 animate-pulse" />}
        {error && !loading && (
          <p className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</p>
        )}

        {!loading && !error && quotes.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <span className="material-symbols-outlined text-gray-300 text-4xl">request_quote</span>
            <p className="text-sm text-gray-500 mt-2">No quotes yet.</p>
            <p className="text-xs text-gray-400 mt-1">When we send you a quote, you'll review and approve it right here.</p>
          </div>
        )}

        {quotes.map(q => (
          <Link key={q.id} to={`/portal/client/quotes/${q.id}`}
            className="block bg-white rounded-xl border border-gray-200 p-4 active:scale-[0.99]">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate">{q.job_title || `Quote ${q.quote_number}`}</p>
                <p className="text-xs text-gray-400 mt-0.5">{q.quote_number}</p>
              </div>
              <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${chip[q.status]?.cls || 'bg-gray-100 text-gray-500'}`}>
                {chip[q.status]?.label || q.status}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-base font-black text-[#C77D3F]">{fmt(q.total)}</span>
              <span className="flex items-center gap-1 text-xs font-semibold text-[#C77D3F]">
                {q.status === 'sent' ? 'Review & respond' : 'View'}
                <span className="material-symbols-outlined text-sm">arrow_forward_ios</span>
              </span>
            </div>
          </Link>
        ))}
      </div>
    </PullToRefresh>
  );
};
